/* eslint-disable no-await-in-loop */
import { lazy } from 'react';
import { logger } from 'services';
import {
  captureChunkErrorTelemetry,
  markChunkErrorReloadAttempt,
  reloadAfterChunkError,
  wasRecentlyReloadedForChunkError,
} from 'utils/chunk-error';

/**
 * Wrapper around React.Lazy.
 * Refresh the page when there is a chunk load error
 * Throws error when the error persists even after refresh
 * @param  {} componentImport
 */
export const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageRefreshed = JSON.parse(
      window.localStorage.getItem('page-refreshed') || 'false',
    );

    try {
      const component = await componentImport();

      window.localStorage.setItem('page-refreshed', 'false');

      return component;
    } catch (error) {
      const errorName = (error as Error)?.name;
      const errorMessage = (error as Error)?.message;
      const messageIsString = typeof errorMessage === 'string';
      const isChunkError =
        errorName === 'ChunkLoadError' ||
        (messageIsString && errorMessage.includes('Loading chunk')) ||
        (messageIsString && errorMessage.includes('Loading CSS chunk'));

      // Only chunk-load errors are reload-recoverable. Non-chunk errors
      // (TypeError inside the lazy module, MF remoteEntry network refusal,
      // syntax error in a bundled module, etc.) won't be fixed by a fresh
      // request — and worse, they don't satisfy `isChunkError`, so the
      // sessionStorage debounce marker below is never set. Combined with
      // the fact that the `page-refreshed` localStorage flag is reset to
      // `false` by the very next successful lazy import (line 26 above),
      // a non-chunk failure during boot kicks off an infinite reload
      // cycle: each iteration adds `?__cb=…`, App.tsx's
      // `stripCacheBusterParam` strips it on mount, the same lazy import
      // throws the same error, and we reload again. Visually it looks
      // like the URL "circles back" between the clean path and the
      // cache-buster query.
      if (
        isChunkError &&
        !wasRecentlyReloadedForChunkError() &&
        !pageRefreshed
      ) {
        // Auto-recovery path: about to reload, ~100% of these resolve on the
        // next page boot. Suppress the Datadog/RUM error log here — the
        // `chunk-error-reload-outcome` LogRocket track event captures the
        // recovery in the next session, and the terminal "already reloaded"
        // branch below still logs the actionable failures. Logging every
        // auto-recovery dominated chunk-error volume without being
        // actionable (PR #17601 follow-up).
        markChunkErrorReloadAttempt();
        window.localStorage.setItem('page-refreshed', 'true');
        reloadAfterChunkError(undefined, {
          name: errorName,
          message: messageIsString ? errorMessage : undefined,
        });

        // Keep the lazy promise pending so React stays in Suspense until the
        // browser reload replaces the page. Resolving with undefined here
        // causes React.lazy to read `.default` on undefined and crash.
        return new Promise<never>(() => {});
      }

      // Terminal path: auto-reload already happened, chunk still failing.
      // This is the actionable signal — log it with the diagnostic
      // telemetry so we can slice failures by CDN / cache / SW state.
      const telemetry = captureChunkErrorTelemetry(error);

      logger.error(
        isChunkError
          ? 'ChunkLoadError: failed to load chunk after reload, giving up'
          : 'Error occured when trying to load chunk',
        { error, ...telemetry },
      );

      throw error;
    }
  });

const retryForImport = async (s: number, fn: () => Promise<any>) => {
  const wait = () => new Promise((res) => setTimeout(res, s * 1000));

  await wait();

  return fn();
};

const exponentRetry = (
  exponent: number,
  fn: () => Promise<any>,
  retryAfterSecond: number,
) =>
  retryForImport(retryAfterSecond ** exponent, () => {
    return fn();
  });

export const exponentialRetryForImport = async (
  fn: () => Promise<any>,
  retryTimes = 3,
  retryAfterSecond = 3,
): Promise<void> => {
  let retryCount = 0;

  let retry = true;

  while (retry) {
    if (retryCount >= retryTimes) {
      retry = false;
    }

    await exponentRetry(retryCount, fn, retryAfterSecond);
    retryCount += 1;
  }
};

type ImportFn<T> = () => Promise<T>;

export async function retryImportInsideRoutesDefinition<
  T extends Record<string, any>,
>(importFn: ImportFn<T>, retries = 3, delay = 1000): Promise<T> {
  let attempt = 0;

  while (attempt < retries) {
    try {
      return await importFn();
    } catch (error) {
      logger.warn(
        `Chunk load failed. Retrying... (${attempt + 1}/${retries})`,
        error,
      );

      // eslint-disable-next-line no-plusplus
      attempt++;
      if (attempt >= retries) {
        logger.error('All retry attempts failed. Reloading the page...', {
          error,
        });
        window.location.reload(); // Force page reload to fetch new bundles
        throw error; // This won't be reached because the page reloads
      }

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error(
    'Unreachable code: retry logic should handle failure or success.',
  );
}
